import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL, getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Users, PlayCircle, BarChart3, ListChecks, Award,
  ChevronRight, ChevronUp, ChevronDown, Star, Trash2, Edit, Save, CalendarDays, ClipboardList, ImagePlus, Camera, FileText, GitCompareArrows, Brain, Sparkles, Loader2, Mail, Send, UserPlus, Handshake, Timer
} from 'lucide-react';
import { MeetGreetPrompt } from '@/components/MeetGreetPrompt';
import { TokenBoard } from '@/components/TokenBoard';
import { SessionTimer } from '@/components/SessionTimer';

const SKILL_PRESETS = [
  { name: 'Blow Bubbles', icon: 'bubbles' },
  { name: 'Kick Legs', icon: 'kick' },
  { name: 'Front Float', icon: 'float' },
  { name: 'Back Float', icon: 'backfloat' },
  { name: 'Arm Strokes', icon: 'strokes' },
  { name: 'Treading Water', icon: 'tread' },
  { name: 'Submerge Face', icon: 'submerge' },
  { name: 'Jump In', icon: 'jump' },
];

export default function InstructorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [sessions, setSessions] = useState({});
  const [feedback, setFeedback] = useState({});
  const [showAddChild, setShowAddChild] = useState(false);
  const [showEditChild, setShowEditChild] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childForm, setChildForm] = useState({ name: '', parent_email: '', notes: '' });
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [sessionSkills, setSessionSkills] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [ifCondition, setIfCondition] = useState('');
  const [thenReward, setThenReward] = useState('');
  const [defaultSkills, setDefaultSkills] = useState([]);
  const [defSkillName, setDefSkillName] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteChildId, setInviteChildId] = useState('');
  const [inviteSending, setInviteSending] = useState(false);

  useEffect(() => { fetchChildren(); }, []);

  const fetchChildren = async () => {
    try {
      const res = await api.get('/children');
      setChildren(res.data);
      const sessMap = {};
      const fbMap = {};
      for (const child of res.data) {
        const sessRes = await api.get(`/sessions?child_id=${child.child_id}`);
        sessMap[child.child_id] = sessRes.data;
        try {
          const fbRes = await api.get(`/feedback?child_id=${child.child_id}`);
          fbMap[child.child_id] = fbRes.data;
        } catch { fbMap[child.child_id] = []; }
      }
      setSessions(sessMap);
      setFeedback(fbMap);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleAddChild = async () => {
    try {
      const res = await api.post('/children', childForm);
      // Upload profile picture if selected
      if (profilePicFile) {
        const formData = new FormData();
        formData.append('file', profilePicFile);
        const imgRes = await api.post('/skill-images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        await api.put(`/children/${res.data.child_id}`, { profile_picture: imgRes.data.filename });
      }
      toast.success('Child added!');
      setShowAddChild(false);
      setChildForm({ name: '', parent_email: '', notes: '' });
      setProfilePicFile(null);
      setProfilePicPreview(null);
      fetchChildren();
    } catch (err) { toast.error(getErrorMessage(err, 'Failed to add child')); }
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfilePicFile(file);
    setProfilePicPreview(URL.createObjectURL(file));
  };

  const openEditChild = (child) => {
    setSelectedChild(child);
    setChildForm({ name: child.name, parent_email: child.parent_email || '', notes: child.notes || '' });
    setProfilePicFile(null);
    setProfilePicPreview(child.profile_picture ? `${BACKEND_URL}/api/uploads/${child.profile_picture}` : null);
    setShowEditChild(true);
  };

  const handleEditChild = async () => {
    try {
      let updates = { ...childForm };
      if (profilePicFile) {
        const formData = new FormData();
        formData.append('file', profilePicFile);
        const imgRes = await api.post('/skill-images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        updates.profile_picture = imgRes.data.filename;
      }
      await api.put(`/children/${selectedChild.child_id}`, updates);
      toast.success('Profile updated!');
      setShowEditChild(false);
      setProfilePicFile(null);
      setProfilePicPreview(null);
      fetchChildren();
    } catch (err) { toast.error(getErrorMessage(err, 'Failed to update')); }
  };

  const [confirmDeleteChild, setConfirmDeleteChild] = useState(null);

  const handleDeleteChild = async (childId) => {
    try {
      await api.delete(`/children/${childId}`);
      toast.success('Child removed');
      setConfirmDeleteChild(null);
      fetchChildren();
    } catch { toast.error('Failed to delete'); }
  };

  // --- Default Visual Schedule ---
  const openScheduleManager = (child) => {
    setSelectedChild(child);
    setDefaultSkills(child.default_skills || []);
    setShowSchedule(true);
  };

  const addDefaultSkillPreset = (preset) => {
    setDefaultSkills(prev => [...prev, {
      skill_id: `ds_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      name: preset.name, order: prev.length + 1, icon: preset.icon
    }]);
  };

  const addCustomDefaultSkill = () => {
    if (!defSkillName.trim()) return;
    setDefaultSkills(prev => [...prev, {
      skill_id: `ds_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      name: defSkillName, order: prev.length + 1
    }]);
    setDefSkillName('');
  };

  const removeDefaultSkill = (idx) => setDefaultSkills(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));

  const handleDefaultSkillImage = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/skill-images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDefaultSkills(prev => prev.map((s, i) => i === idx ? { ...s, image: res.data.filename } : s));
      toast.success('Image added to skill!');
    } catch { toast.error('Failed to upload image'); }
  };

  const saveDefaultSchedule = async () => {
    try {
      await api.put(`/children/${selectedChild.child_id}`, { default_skills: defaultSkills });
      toast.success('Default schedule saved! New sessions will use this schedule.');
      setShowSchedule(false);
      fetchChildren();
    } catch { toast.error('Failed to save'); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim() || !inviteChildId) {
      toast.error('Please enter email and select a child');
      return;
    }
    setInviteSending(true);
    try {
      await api.post('/invites', { email: inviteEmail.trim().toLowerCase(), child_id: inviteChildId });
      toast.success(`Invite sent to ${inviteEmail}!`);
      setShowInvite(false);
      setInviteEmail('');
      setInviteChildId('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    }
    setInviteSending(false);
  };

  const assignParent = async (childId, email) => {
    try {
      const res = await api.post(`/children/${childId}/assign-parent`, { email });
      toast.success(`Linked ${res.data.parent_name} as parent!`);
      fetchChildren();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign parent');
    }
  };

  // --- Session creation ---
  const openNewSession = (child) => {
    setSelectedChild(child);
    // Pre-populate from default schedule with images
    const defSkills = (child.default_skills || []).map((ds, idx) => ({
      skill_id: `skill_${Date.now()}_${Math.random().toString(36).slice(2,6)}_${idx}`,
      name: ds.name, order: idx + 1, completed: false, icon: ds.icon, image: ds.image || null
    }));
    setSessionSkills(defSkills);
    setShowNewSession(true);
    // Fetch AI suggestions in background
    setAiSuggestions(null);
    setAiLoading(true);
    api.get(`/children/${child.child_id}/ai-session-suggestions`)
      .then(res => setAiSuggestions(res.data))
      .catch(() => {})
      .finally(() => setAiLoading(false));
  };

  const addSkillPreset = (preset) => {
    setSessionSkills(prev => [...prev, {
      skill_id: `skill_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      name: preset.name, order: prev.length + 1, completed: false, icon: preset.icon
    }]);
  };

  const addCustomSkill = () => {
    if (!newSkillName.trim()) return;
    setSessionSkills(prev => [...prev, {
      skill_id: `skill_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      name: newSkillName, order: prev.length + 1, completed: false
    }]);
    setNewSkillName('');
  };

  const removeSkill = (idx) => setSessionSkills(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));

  const moveSkill = (idx, direction) => {
    setSessionSkills(prev => {
      const arr = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleCreateSession = async () => {
    if (!selectedChild || sessionSkills.length === 0) {
      toast.error('Add at least one skill');
      return;
    }
    // Check if criteria exist for this child
    try {
      const critRes = await api.get(`/criteria?child_id=${selectedChild.child_id}`);
      if (!critRes.data || critRes.data.length === 0) {
        toast.error('Please create assessment criteria for this child before starting a session. Go to the Criteria page first.');
        return;
      }
    } catch {
      toast.error('Failed to check criteria. Please try again.');
      return;
    }
    try {
      const res = await api.post('/sessions', {
        child_id: selectedChild.child_id,
        skills: sessionSkills,
        if_condition: ifCondition || null,
        then_reward: thenReward || null,
        local_date: new Date().toLocaleDateString('en-CA'),
      });
      toast.success(`Session #${res.data.session_number} created!`);
      setShowNewSession(false);
      setSessionSkills([]);
      setIfCondition('');
      setThenReward('');
      fetchChildren();
      navigate(`/dashboard/session/${res.data.session_id}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create session'));
    }
  };

  const getActiveSession = (childId) => {
    const cs = sessions[childId] || [];
    return cs.find(s => s.status === 'active') || cs.find(s => s.status === 'planned');
  };

  if (loading) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;

  // Collect all active sessions across children for the widget
  const activeSessions = children.reduce((acc, child) => {
    const cs = sessions[child.child_id] || [];
    const active = cs.find(s => s.status === 'active');
    if (active) acc.push({ ...active, child_name: child.name, child_id: child.child_id, profile_picture: child.profile_picture });
    return acc;
  }, []);

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn" data-testid="instructor-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-[#1E1B4B] tracking-tight" style={{ fontFamily: 'Nunito' }}>
            Hi, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 mt-1">Manage your swim students and sessions</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="invite-parent-btn" onClick={() => setShowInvite(true)} variant="outline" className="rounded-full border-[#1E1B4B] text-[#1E1B4B] font-bold px-5 gap-2">
            <Mail className="w-4 h-4" /> Invite Parent
          </Button>
          <Dialog open={showAddChild} onOpenChange={setShowAddChild}>
            <DialogTrigger asChild>
              <Button data-testid="add-child-btn" className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#1E1B4B] font-bold px-6 shadow-lg gap-2">
                <Plus className="w-5 h-5" /> Add Student
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Add New Student</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Profile Picture Upload */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  {profilePicPreview ? (
                    <img src={profilePicPreview} alt="Preview" className="w-20 h-20 rounded-2xl object-cover border-2 border-[#22D3EE]/30" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#ECFEFF] to-[#A5F3FC] flex items-center justify-center">
                      <Camera className="w-8 h-8 text-[#0E7490]/40" />
                    </div>
                  )}
                  <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#22D3EE] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#06B6D4] transition-colors shadow-md" data-testid="add-profile-pic-btn">
                    <ImagePlus className="w-3.5 h-3.5 text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
                  </label>
                </div>
                <div className="flex-1">
                  <Label className="font-semibold text-slate-600">Profile Photo</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Add a photo of the child (optional)</p>
                </div>
              </div>
              <div>
                <Label className="font-semibold text-slate-600">Child's Name</Label>
                <Input data-testid="child-name-input" className="mt-1 rounded-xl" placeholder="e.g., Alex" value={childForm.name} onChange={e => setChildForm({ ...childForm, name: e.target.value })} />
              </div>
              <div>
                <Label className="font-semibold text-slate-600">Parent's Email (optional, can add later)</Label>
                <Input data-testid="parent-email-input" className="mt-1 rounded-xl" placeholder="parent@email.com" value={childForm.parent_email} onChange={e => setChildForm({ ...childForm, parent_email: e.target.value })} />
              </div>
              <div>
                <Label className="font-semibold text-slate-600">Notes</Label>
                <Textarea data-testid="child-notes-input" className="mt-1 rounded-xl" placeholder="Sensory preferences, triggers, etc." value={childForm.notes} onChange={e => setChildForm({ ...childForm, notes: e.target.value })} />
              </div>
              <Button data-testid="save-child-btn" onClick={handleAddChild} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#1E1B4B] font-bold">Save Student</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Active Sessions Widget */}
      {activeSessions.length > 0 && (
        <div className="mb-6" data-testid="active-sessions-widget">
          <div className="flex items-center gap-2 mb-3">
            <Timer className="w-5 h-5 text-green-500" />
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Live Sessions</h2>
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {activeSessions.map(s => (
              <button
                key={s.session_id}
                onClick={() => navigate(`/dashboard/session/${s.session_id}`)}
                className="flex items-center gap-3 bg-white rounded-2xl border border-green-200 p-4 hover:shadow-md hover:border-green-300 transition-all text-left group"
                data-testid={`active-session-widget-${s.session_id}`}
              >
                {s.profile_picture ? (
                  <img src={`${BACKEND_URL}/api/uploads/${s.profile_picture}`} alt={s.child_name} className="w-11 h-11 rounded-xl object-cover border-2 border-green-100 shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                    <span className="text-lg font-extrabold text-green-600" style={{ fontFamily: 'Nunito' }}>{s.child_name?.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm truncate" style={{ fontFamily: 'Nunito' }}>{s.child_name}</p>
                  <p className="text-xs text-slate-400">Session #{s.session_number} &middot; {s.skills?.filter(sk => sk.completed).length}/{s.skills?.length} skills</p>
                </div>
                <div className="shrink-0">
                  <SessionTimer startedAt={s.started_at} compact />
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-green-500 transition-colors shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {children.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No students yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {children.map((child) => {
            const childSessions = sessions[child.child_id] || [];
            const active = getActiveSession(child.child_id);
            const completedSessions = childSessions.filter(s => s.status === 'completed');
            const completed = completedSessions.length;
            const recentCompleted = [...completedSessions].sort((a, b) => (b.session_number || 0) - (a.session_number || 0)).slice(0, 4);
            const hasDefaultSchedule = (child.default_skills || []).length > 0;

            return (
              <Card key={child.child_id} className="rounded-3xl border-slate-100 shadow-sm card-hover overflow-hidden" data-testid={`child-card-${child.child_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {child.profile_picture ? (
                        <img src={`${BACKEND_URL}/api/uploads/${child.profile_picture}`} alt={child.name} className="w-12 h-12 rounded-2xl object-cover border-2 border-[#22D3EE]/20" />
                      ) : (
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#ECFEFF] to-[#A5F3FC] flex items-center justify-center">
                          <span className="text-xl font-extrabold text-[#0E7490]" style={{ fontFamily: 'Nunito' }}>{child.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Nunito' }}>{child.name}</CardTitle>
                        <p className="text-xs text-slate-400 mt-0.5">{child.parent_email || 'No parent linked'}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-[#22D3EE] w-9 h-9" onClick={() => openEditChild(child)} data-testid={`edit-child-${child.child_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500 w-9 h-9" onClick={() => setConfirmDeleteChild(child)} data-testid={`delete-child-${child.child_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {child.notes && <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3 line-clamp-2">{child.notes}</p>}

                  {/* Meet & Greet Status */}
                  <MeetGreetPrompt childId={child.child_id} childName={child.name} userRole="instructor" />
                  <TokenBoard childId={child.child_id} childName={child.name} />

                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><PlayCircle className="w-4 h-4" /> {childSessions.length} sessions</span>
                    <span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#C4F017]" /> {completed} completed</span>
                  </div>

                  {/* Default schedule indicator */}
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`rounded-full cursor-pointer ${hasDefaultSchedule ? 'border-[#22D3EE]/30 text-[#0E7490] bg-[#ECFEFF]' : 'border-amber-200 text-amber-600 bg-amber-50'}`}
                      onClick={() => openScheduleManager(child)}
                      data-testid={`schedule-badge-${child.child_id}`}
                    >
                      <CalendarDays className="w-3 h-3 mr-1" />
                      {hasDefaultSchedule ? `${child.default_skills.length} default skills` : 'Set default schedule'}
                    </Badge>
                    {active && (
                      <Badge variant="outline" className="border-green-200 text-green-700 bg-green-50 rounded-full">
                        {active.status === 'active' ? 'Active' : 'Planned'}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                    {active ? (
                      <Button data-testid={`resume-session-${child.child_id}`} onClick={() => navigate(`/dashboard/session/${active.session_id}`)} className="rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold gap-1 col-span-2 sm:col-span-3 h-12">
                        <PlayCircle className="w-5 h-5" /> {active.status === 'active' ? 'Resume' : 'Start'} Session
                      </Button>
                    ) : (
                      <Button data-testid={`new-session-${child.child_id}`} onClick={() => openNewSession(child)} className="rounded-xl bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-1 col-span-2 sm:col-span-3 h-12">
                        <Plus className="w-5 h-5" /> New Session
                      </Button>
                    )}
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 h-11" onClick={() => navigate(`/dashboard/progress/${child.child_id}`)} data-testid={`view-progress-${child.child_id}`}>
                      <BarChart3 className="w-4 h-4" /> Progress
                    </Button>
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 h-11" onClick={() => navigate(`/dashboard/criteria/${child.child_id}`)} data-testid={`view-criteria-${child.child_id}`}>
                      <ListChecks className="w-4 h-4" /> Criteria
                    </Button>
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 h-11" onClick={() => navigate(`/dashboard/reports/${child.child_id}`)} data-testid={`view-reports-${child.child_id}`}>
                      <FileText className="w-4 h-4" /> Reports
                    </Button>
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 border-[#22D3EE]/40 text-[#0E7490] hover:bg-[#ECFEFF] h-11" onClick={() => navigate(`/dashboard/compare/${child.child_id}`)} data-testid={`compare-sessions-${child.child_id}`}>
                      <GitCompareArrows className="w-4 h-4" /> Compare
                    </Button>
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 border-purple-200 text-purple-700 hover:bg-purple-50 h-11" onClick={() => navigate(`/dashboard/ai-insights/${child.child_id}`)} data-testid={`ai-insights-${child.child_id}`}>
                      <Brain className="w-4 h-4" /> Insights
                    </Button>
                    <Button variant="outline" className="rounded-xl text-xs font-semibold gap-1 border-amber-200 text-amber-700 hover:bg-amber-50 h-11" onClick={() => navigate(`/dashboard/intake/${child.child_id}`)} data-testid={`intake-form-${child.child_id}`}>
                      <ClipboardList className="w-4 h-4" /> Intake Form
                    </Button>
                  </div>

                  {/* Past Sessions - clickable list */}
                  {completedSessions.length > 0 && (
                    <div className="border-t border-slate-100 pt-3 mt-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Past Sessions</p>
                      {recentCompleted.map(s => {
                        const fb = s.session_feedback || {};
                        const preview = fb.effective_strategies || fb.thinking_moment || s.instructor_notes || '';
                        return (
                          <div key={s.session_id} className="p-2.5 bg-slate-50 rounded-xl mb-2 cursor-pointer hover:bg-[#ECFEFF] transition-colors group" onClick={() => navigate(`/dashboard/session/${s.session_id}`)} data-testid={`past-session-${s.session_id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="rounded-full text-[10px] px-1.5 py-0 border-[#22D3EE]/40 text-[#0E7490] font-bold">Session #{s.session_number}</Badge>
                                <span className="text-[10px] text-slate-400">{new Date(s.date || s.created_at).toLocaleDateString()}</span>
                              </div>
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#22D3EE] transition-colors" />
                            </div>
                            {preview && <p className="text-xs text-slate-500 line-clamp-1">{preview}</p>}
                            {!preview && <p className="text-xs text-slate-400 italic">Tap to view session details</p>}
                          </div>
                        );
                      })}
                      {completedSessions.length > 4 && (
                        <p className="text-xs text-center text-slate-400 mt-1">+{completedSessions.length - 4} more sessions</p>
                      )}
                    </div>
                  )}

                  {/* Parent Feedback */}
                  {(feedback[child.child_id] || []).length > 0 && (
                    <div className="border-t border-slate-100 pt-3 mt-1">
                      <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-2">Parent Feedback ({feedback[child.child_id].length})</p>
                      {feedback[child.child_id].slice(0, 3).map(fb => {
                        const relSession = childSessions.find(s => s.session_id === fb.session_id);
                        return (
                          <div key={fb.feedback_id} className="p-2.5 bg-emerald-50 rounded-xl mb-2 cursor-pointer hover:bg-emerald-100 transition-colors" onClick={() => navigate(`/dashboard/session/${fb.session_id}`)} data-testid={`parent-feedback-${fb.feedback_id}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="rounded-full text-[10px] px-1.5 py-0 border-emerald-200 text-emerald-600 font-bold">
                                {relSession ? `#${relSession.session_number}` : 'Session'}
                              </Badge>
                              <span className="text-[10px] text-emerald-500 font-bold">{fb.parent_name || 'Parent'}</span>
                              <span className="text-[10px] text-slate-400">{new Date(fb.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-xs text-slate-600 line-clamp-1">{fb.session_notes || fb.suggestions || fb.engagement_observations || 'Feedback submitted'}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Child Dialog */}
      <Dialog open={showEditChild} onOpenChange={setShowEditChild}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Nunito' }}>Edit {selectedChild?.name}'s Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {profilePicPreview ? (
                  <img src={profilePicPreview} alt="Preview" className="w-20 h-20 rounded-2xl object-cover border-2 border-[#22D3EE]/30" />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#ECFEFF] to-[#A5F3FC] flex items-center justify-center">
                    <Camera className="w-8 h-8 text-[#0E7490]/40" />
                  </div>
                )}
                <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#22D3EE] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#06B6D4] transition-colors shadow-md" data-testid="edit-profile-pic-btn">
                  <ImagePlus className="w-3.5 h-3.5 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
                </label>
              </div>
              <div className="flex-1">
                <Label className="font-semibold text-slate-600">Profile Photo</Label>
                <p className="text-xs text-slate-400 mt-0.5">Update the child's photo</p>
              </div>
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Name</Label>
              <Input data-testid="edit-child-name" className="mt-1 rounded-xl" value={childForm.name} onChange={e => setChildForm({ ...childForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Assigned Parent Email</Label>
              <Input data-testid="edit-parent-email" className="mt-1 rounded-xl" placeholder="parent@email.com" value={childForm.parent_email} onChange={e => setChildForm({ ...childForm, parent_email: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">Change this email to reassign the child to a different parent. Clear it to unlink.</p>
              {childForm.parent_email && childForm.parent_email !== (selectedChild?.parent_email || '') && (
                <p className="text-xs text-amber-600 mt-1 font-medium">Parent will be reassigned when you save</p>
              )}
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Notes</Label>
              <Textarea data-testid="edit-child-notes" className="mt-1 rounded-xl" value={childForm.notes} onChange={e => setChildForm({ ...childForm, notes: e.target.value })} />
            </div>
            <Button data-testid="save-edit-child-btn" onClick={handleEditChild} className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#1E1B4B] font-bold gap-2">
              <Save className="w-4 h-4" /> Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Default Visual Schedule Manager */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>{selectedChild?.name}'s Default Visual Schedule</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">This schedule will be used for every new session. Consistency builds routine!</p>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Skill Presets */}
            <div>
              <Label className="font-semibold text-slate-600 mb-2 block">Add Skills (tap to add)</Label>
              <div className="flex flex-wrap gap-2">
                {SKILL_PRESETS.map(p => (
                  <button key={p.name} onClick={() => addDefaultSkillPreset(p)} className="px-3 py-2 rounded-full bg-[#ECFEFF] text-[#0E7490] text-sm font-semibold hover:bg-[#A5F3FC] transition-colors" data-testid={`def-preset-${p.name}`}>
                    + {p.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Input className="rounded-xl flex-1" placeholder="Custom skill name" value={defSkillName} onChange={e => setDefSkillName(e.target.value)} data-testid="def-custom-skill-input" onKeyDown={e => e.key === 'Enter' && addCustomDefaultSkill()} />
              <Button onClick={addCustomDefaultSkill} className="rounded-xl bg-[#22D3EE] hover:bg-[#06B6D4] text-[#1E1B4B]"><Plus className="w-4 h-4" /></Button>
            </div>

            {defaultSkills.length > 0 && (
              <div>
                <Label className="font-semibold text-slate-600 mb-2 block">Default Schedule ({defaultSkills.length} skills)</Label>
                <div className="flex flex-col gap-2">
                  {defaultSkills.map((skill, idx) => (
                    <div key={skill.skill_id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#22D3EE] w-10">{['FIRST','THEN','NEXT','NEXT','NEXT','NEXT','NEXT','NEXT'][idx] || 'NEXT'}</span>
                      <span className="w-8 h-8 rounded-full bg-[#1E1B4B] text-white flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                      {skill.image ? (
                        <img src={`${BACKEND_URL}/api/uploads/${skill.image}`} alt={skill.name} className="w-10 h-10 rounded-lg object-cover border-2 border-[#22D3EE]/30" />
                      ) : null}
                      <span className="flex-1 font-medium text-slate-700">{skill.name}</span>
                      <label className="w-8 h-8 rounded-full bg-[#22D3EE]/10 flex items-center justify-center cursor-pointer hover:bg-[#22D3EE]/20 transition-colors" title="Add skill photo" data-testid={`def-skill-image-${idx}`}>
                        <ImagePlus className="w-4 h-4 text-[#22D3EE]" />
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleDefaultSkillImage(idx, e)} />
                      </label>
                      <button onClick={() => removeDefaultSkill(idx)} className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={saveDefaultSchedule} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg h-12 gap-2" data-testid="save-default-schedule-btn">
              <Save className="w-5 h-5" /> Save Default Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Session Dialog */}
      <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>New Session for {selectedChild?.name}</DialogTitle>
            {sessionSkills.length > 0 && (selectedChild?.default_skills || []).length > 0 && (
              <p className="text-xs text-[#22D3EE] font-semibold mt-1">Pre-filled from default schedule</p>
            )}
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <Label className="font-semibold text-slate-600 mb-2 block">Add More Skills</Label>
              <div className="flex flex-wrap gap-2">
                {SKILL_PRESETS.map(p => (
                  <button key={p.name} onClick={() => addSkillPreset(p)} className="px-3 py-2 rounded-full bg-[#ECFEFF] text-[#0E7490] text-sm font-semibold hover:bg-[#A5F3FC] transition-colors" data-testid={`skill-preset-${p.name}`}>
                    + {p.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Input className="rounded-xl flex-1" placeholder="Custom skill name" value={newSkillName} onChange={e => setNewSkillName(e.target.value)} data-testid="custom-skill-input" onKeyDown={e => e.key === 'Enter' && addCustomSkill()} />
              <Button onClick={addCustomSkill} className="rounded-xl bg-[#22D3EE] hover:bg-[#06B6D4] text-[#1E1B4B]" data-testid="add-custom-skill-btn"><Plus className="w-4 h-4" /></Button>
            </div>

            {sessionSkills.length > 0 && (
              <div>
                <Label className="font-semibold text-slate-600 mb-2 block">Session Schedule ({sessionSkills.length} skills)</Label>
                <div className="flex flex-col gap-2">
                  {sessionSkills.map((skill, idx) => (
                    <div key={skill.skill_id} className="flex items-center gap-2 bg-slate-50 rounded-xl p-3">
                      {/* Reorder arrows */}
                      <div className="flex flex-col">
                        <button onClick={() => moveSkill(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-20" data-testid={`move-up-${idx}`}><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={() => moveSkill(idx, 1)} disabled={idx === sessionSkills.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-20" data-testid={`move-down-${idx}`}><ChevronDown className="w-4 h-4" /></button>
                      </div>
                      <span className="w-7 h-7 rounded-full bg-[#1E1B4B] text-white flex items-center justify-center text-xs font-bold shrink-0">{idx + 1}</span>
                      {/* Skill photo */}
                      <label className="shrink-0 cursor-pointer">
                        {skill.image ? (
                          <img src={`${BACKEND_URL}/api/uploads/${skill.image}`} alt={skill.name} className="w-10 h-10 rounded-lg object-cover border-2 border-[#22D3EE]/30" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-[#22D3EE] hover:bg-[#ECFEFF] transition-colors">
                            <Camera className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          fd.append('skill_name', skill.name);
                          try {
                            const res = await api.post('/skill-images/upload', fd);
                            const filename = res.data.filename;
                            // Update this skill's image
                            setSessionSkills(prev => prev.map((s, i) => i === idx ? { ...s, image: filename } : s));
                            // Also save as default for this skill name on the child
                            if (selectedChild) {
                              await api.put(`/children/${selectedChild.child_id}/default-skill-image`, { skill_name: skill.name, image: filename }).catch(() => {});
                            }
                            toast.success('Photo added!');
                          } catch { toast.error('Upload failed'); }
                        }} />
                      </label>
                      <span className="flex-1 font-medium text-slate-700 text-sm truncate">{skill.name}</span>
                      <button onClick={() => removeSkill(idx)} className="text-slate-400 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#F7FEE7] rounded-2xl p-4 space-y-3">
              <Label className="font-semibold text-lime-700 flex items-center gap-2"><Award className="w-5 h-5 text-[#C4F017]" /> If/Then Reward (optional)</Label>
              <Input className="rounded-xl" placeholder="IF... (e.g., I complete all skills)" value={ifCondition} onChange={e => setIfCondition(e.target.value)} data-testid="if-condition-input" />
              <Input className="rounded-xl" placeholder="THEN... (e.g., I can play in the hot tub)" value={thenReward} onChange={e => setThenReward(e.target.value)} data-testid="then-reward-input" />
              {aiSuggestions?.reward_suggestion && !thenReward && (
                <button
                  className="text-xs text-lime-600 hover:text-lime-700 flex items-center gap-1"
                  onClick={() => {
                    const parts = aiSuggestions.reward_suggestion.split(' THEN ');
                    if (parts.length === 2) { setIfCondition(parts[0].replace('IF ', '')); setThenReward(parts[1]); }
                    else { setThenReward(aiSuggestions.reward_suggestion); }
                  }}
                  data-testid="ai-reward-suggestion"
                >
                  <Sparkles className="w-3 h-3" /> AI suggests: {aiSuggestions.reward_suggestion.substring(0, 60)}...
                </button>
              )}
            </div>

            {/* AI Session Suggestions */}
            {(aiLoading || aiSuggestions) && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-4 space-y-3 border border-purple-100" data-testid="ai-session-suggestions">
                <div className="flex items-center gap-2">
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                  ) : (
                    <Brain className="w-4 h-4 text-purple-500" />
                  )}
                  <span className="text-sm font-bold text-purple-700">AI Session Suggestions</span>
                </div>
                {aiLoading && <p className="text-xs text-purple-400">Analyzing child's history...</p>}
                {aiSuggestions && !aiSuggestions.parsing_error && (
                  <>
                    {aiSuggestions.session_tips?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-purple-500 uppercase mb-1">Tips</p>
                        <ul className="space-y-1">
                          {aiSuggestions.session_tips.slice(0, 3).map((tip, i) => (
                            <li key={`idl-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5">
                              <Sparkles className="w-3 h-3 text-purple-400 mt-0.5 shrink-0" /> {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aiSuggestions.equipment_needed?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-purple-500 uppercase mb-1">Equipment</p>
                        <div className="flex flex-wrap gap-1">
                          {aiSuggestions.equipment_needed.map((eq, i) => (
                            <Badge key={`idb-${i}`} className="rounded-full bg-purple-100 text-purple-700 text-[10px] font-normal">{eq}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {aiSuggestions.caution_flags?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-amber-500 uppercase mb-1">Watch For</p>
                        <ul className="space-y-1">
                          {aiSuggestions.caution_flags.slice(0, 2).map((flag, i) => (
                            <li key={`idl-${i}`} className="text-xs text-amber-700 flex items-start gap-1.5">
                              <span className="text-amber-400 shrink-0">!</span> {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <Button onClick={handleCreateSession} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg h-12" data-testid="create-session-btn">
              Create Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDeleteChild} onOpenChange={() => setConfirmDeleteChild(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>Delete Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{confirmDeleteChild?.name}</strong>? This will remove all their sessions, assessments, and data. This action cannot be undone.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setConfirmDeleteChild(null)} data-testid="cancel-delete-btn">
                Cancel
              </Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={() => handleDeleteChild(confirmDeleteChild.child_id)} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Parent Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <Mail className="w-5 h-5 text-[#22D3EE]" /> Invite Parent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">Send an invite to a parent. Their account will be linked to the selected child's profile.</p>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-1 block">Parent Email</Label>
              <Input className="rounded-xl" type="email" placeholder="parent@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-1 block">Link to Child</Label>
              <select
                className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm"
                value={inviteChildId}
                onChange={e => setInviteChildId(e.target.value)}
                data-testid="invite-child-select"
              >
                <option value="">Select a child...</option>
                {children.map(c => <option key={c.child_id} value={c.child_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={sendInvite}
                disabled={inviteSending}
                className="flex-1 rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
                data-testid="send-invite-btn"
              >
                {inviteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
